/**
 * R-16 T-135 (design v1.2 change 02 §5) — the quick-links row (layer 3): the reader's own shortcuts between the app bar and
 * the breadcrumb, at the gutter, scrolling with the page (sideways inside itself below 768). Up to ten links, the rest
 * under More ▾, then Edit quick links. The list is kept on the server per person per app (GET/PUT
 * /api/me/quick-links/{appSlug}); a star in any menu adds a page to the end, a filled star removes it. Only pages in the
 * menus the reader can see ever show — hidden by role, permission or tier means absent here, in the menus and in the editor.
 */
import { createContext, useContext, useEffect, useLayoutEffect, useMemo, useReducer, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { toast } from 'sonner';
import { ChevronDown } from 'lucide-react';
import { Button, Menu, MenuItem, useDisclosure } from '@/kit';
import type { MenuDecl } from '@/features/apps/apps-api';
import { useMe } from '@/api/queries';
import { useViewAs } from '@/app/experience/experience-api';
import { useQuickLinks, useSendQuickLinks } from './api';
import { editorList, fitCount, quickLinkItems, ROW_MAX, SaveQueue } from './model';
import { QuickLinksEditor } from './QuickLinksEditor';

interface QuickLinksValue {
  app: string;
  /** the list as kept (tier-hidden pages included); the row and the menus show only what their tier-cut menus hold */
  stored: string[];
  /** while viewing as another person: why the star and the editor do nothing (T-088, the administrator's own list is kept) */
  locked: string | null;
  defaults: string[];
  has: (id: string) => boolean;
  toggle: (id: string) => void;
  save: (ids: string[] | null) => Promise<unknown>;
  saving: boolean;
}

const Ctx = createContext<QuickLinksValue | null>(null);

/** The star a menu item shows: null outside an app, while the row is hidden at the reader's tier, or before the list loads. */
export function useQuickLinkStar(): Pick<QuickLinksValue, 'has' | 'toggle' | 'locked'> | null {
  const v = useContext(Ctx);
  const shown = useDisclosure('nav.quick_links');
  return v && shown ? v : null;
}

export function QuickLinksProvider({ app, children }: { app: string | null; children: React.ReactNode }) {
  const q = useQuickLinks(app);
  const send = useSendQuickLinks(app);
  const [version, changed] = useReducer((n: number) => n + 1, 0);
  const [saving, setSaving] = useState(false);
  // PR #88 round 2: one queue per app — the star reads and changes the stored list; saves go one at a time
  const queue = useRef<{ app: string; q: SaveQueue } | null>(null);
  if (app && q.data && queue.current?.app !== app) queue.current = { app, q: new SaveQueue(q.data.stored, (ids) => send(ids), changed) };
  useEffect(() => {
    if (q.data && queue.current) queue.current.q.sync(q.data.stored);
  }, [q.data]);
  const me = useMe(true);
  const viewAs = useViewAs(Boolean(me.data));
  const locked = viewAs.data?.active ? 'Your own quick links are kept while you view as someone else; stop viewing as to change them.' : null;
  // `version` changes after every queue change, so the value re-reads the stored list
  const value = useMemo<QuickLinksValue | null>(() => {
    const kept = queue.current;
    if (!app || !q.data || !kept || kept.app !== app) return null;
    const track = <T,>(p: Promise<T>) => {
      setSaving(true);
      return p.finally(() => setSaving(false));
    };
    return {
      app,
      stored: kept.q.stored,
      locked,
      defaults: q.data.defaults,
      // PR #88: the star reads and changes the list as kept, so a page the tier hid a moment ago is never lost
      has: (id) => kept.q.has(id),
      // a refused star rolls back (the queue) and says why
      toggle: (id) => void (locked ? undefined : track(kept.q.toggle(id)).catch((e: unknown) => toast.error('Your quick links were not saved', { description: e instanceof Error ? e.message : undefined }))),
      save: (next) => track(kept.q.save(next)),
      saving,
    };
  }, [app, q.data, locked, version, saving]);
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

const pathOf = (route: string) => route.split(/[?#]/)[0] ?? '';

/** below 768 menu.css lets the strip scroll sideways inside itself: then nothing moves under More */
const SCROLLS = new Set(['auto', 'scroll']);
const LINK_GAP = 2;
const STRIP_BLEED = 8; // the strip's -8px left margin: the first link's text sits at the gutter
const TAIL_GAP = 4;

/** PR #88 round 4: how many of the row's links fit beside the tail at this width (null below 768: the row scrolls). */
function useFit(key: string, editAbsent: boolean) {
  const rowRef = useRef<HTMLDivElement | null>(null);
  const measureRef = useRef<HTMLDivElement | null>(null);
  const tailRef = useRef<HTMLDivElement | null>(null);
  const [count, setCount] = useState<number | null>(null);
  // re-measured when the links or the tail change (`key`, `editAbsent`) and on every resize
  useLayoutEffect(() => {
    const rowEl = rowRef.current;
    const measure = measureRef.current;
    if (!rowEl || !measure || typeof window === 'undefined') return;
    const update = () => {
      const nav = rowEl.querySelector<HTMLElement>('.k-ql-in');
      const strip = rowEl.querySelector<HTMLElement>('.k-ql-links');
      if (!nav || !strip) return;
      if (SCROLLS.has(getComputedStyle(strip).overflowX)) return setCount(null);
      const cs = getComputedStyle(nav);
      const inner = nav.clientWidth - Number.parseFloat(cs.paddingLeft) - Number.parseFloat(cs.paddingRight);
      const edit = tailRef.current?.querySelector<HTMLElement>('[data-testid="quick-links-edit"]')?.offsetWidth ?? 0;
      const kids = Array.from(measure.children) as HTMLElement[];
      const moreW = kids.find((k) => k.hasAttribute('data-m'))?.offsetWidth ?? 0;
      const widths = kids.filter((k) => !k.hasAttribute('data-m')).map((k) => k.getBoundingClientRect().width);
      const available = inner + STRIP_BLEED - (edit ? edit + TAIL_GAP : 0);
      setCount(fitCount(widths, available, LINK_GAP, moreW + TAIL_GAP));
    };
    update();
    const ro = new ResizeObserver(update);
    ro.observe(rowEl);
    return () => ro.disconnect();
  }, [key, editAbsent]);
  return { rowRef, measureRef, tailRef, count };
}

export function QuickLinksRow({ menus }: { menus: readonly MenuDecl[] }) {
  const v = useContext(Ctx);
  const shown = useDisclosure('nav.quick_links');
  const canEdit = useDisclosure('nav.quick_links.edit');
  const { pathname } = useLocation();
  const [editing, setEditing] = useState(false);
  const items = v ? quickLinkItems(v.stored, menus) : [];
  const candidates = items.slice(0, ROW_MAX);
  const fit = useFit(candidates.map((it) => it.id).join('\n'), Boolean(v?.locked) || !canEdit);
  if (!shown || !v) return null;
  const visible = new Set(items.map((it) => it.id));
  // PR #88 round 4: from 768 the row shows what fits and moves the rest under More; below it scrolls inside itself
  const n = fit.count ?? candidates.length;
  const row = candidates.slice(0, n);
  const more = items.slice(n);
  return (
    <div className="k-ql" data-kit="quick-links-row" data-testid="quick-links-row" ref={fit.rowRef}>
      {/* the links at their natural widths, out of sight, so the row knows what fits */}
      <div className="k-ql-measure" aria-hidden ref={fit.measureRef}>
        {candidates.map((it) => (
          <span key={it.id} className="k-ql-link">
            {it.label}
          </span>
        ))}
        <span className="k-btn" data-m>
          More <ChevronDown aria-hidden />
        </span>
      </div>
      <nav aria-label="Quick links" className="k-ql-in k-gutter" data-testid="quick-links">
        <div className="k-ql-links">
          {row.length === 0 ? <span className="k-ql-empty">Star a page in any menu to add it here.</span> : null}
          {row.map((it) => (
            <Link key={it.id} to={it.route} className="k-ql-link" data-ql-id={it.id} data-testid={`quick-link-${it.id}`} aria-current={pathOf(it.route) === pathname ? 'page' : undefined}>
              {it.label}
            </Link>
          ))}
        </div>
        <div className="k-ql-tail" ref={fit.tailRef}>
          {more.length ? (
            <Menu
              align="end"
              aria-label="More quick links"
              trigger={
                <Button variant="quiet" data-testid="quick-links-more">
                  More <ChevronDown aria-hidden />
                </Button>
              }
            >
              {more.map((it) => (
                <MenuItem key={it.id} render={<Link to={it.route} data-ql-id={it.id} />} data-testid={`quick-link-${it.id}`}>
                  {it.label}
                </MenuItem>
              ))}
            </Menu>
          ) : null}
          {canEdit && !v.locked ? (
            <Button variant="quiet" data-testid="quick-links-edit" onClick={() => setEditing(true)}>
              Edit quick links
            </Button>
          ) : null}
        </div>
      </nav>
      {editing ? <QuickLinksEditor items={items} defaults={quickLinkItems(v.defaults, menus)} saving={v.saving} onSave={(ids, reset) => v.save(ids === null ? null : editorList(ids, reset, v.stored, visible))} onClose={() => setEditing(false)} /> : null}
    </div>
  );
}
